import { useState } from 'react';

/**
 * A taxonomy-resolved skill chip: the canonical node id, its label, and whether
 * it still counts (false = removed, shown struck-through and re-addable). Both
 * the tender overview (`TenderSkillAPIV2`) and the employee view's CV skills are
 * structurally this shape.
 */
export interface SkillChip {
  id: string;
  name: string;
  active: boolean;
}

/**
 * Deactivation reasons (storage spec §4.5 controlled vocabulary). Shown in the
 * popup when removing a skill; the value is sent to the backend, the label is for
 * the recruiter.
 */
const SKILL_REMOVAL_REASONS: { value: string; label: string }[] = [
  { value: 'not_required', label: 'Not a real requirement' },
  { value: 'not_a_skill', label: 'Not a skill' },
  { value: 'not_present', label: 'Not present in the source' },
];

/**
 * One labelled row of skill chips (Active / Inactive). Each chip carries a
 * toggle: × deactivates an active skill (after declaring a reason), + activates
 * an inactive one. The toggle is a recruiter override the backend keeps
 * independent of how the skill was sourced.
 */
export function SkillRow({
  label,
  items,
  emptyText,
  tone,
  onToggle,
}: {
  label: string;
  items: SkillChip[];
  emptyText: string;
  tone: 'active' | 'inactive';
  onToggle: (skillId: string, nextActive: boolean, reason?: string) => void;
}) {
  // Which skill's "why remove?" reason popup is open, if any.
  const [reasonForSkill, setReasonForSkill] = useState<string | null>(null);
  const chipClass =
    tone === 'active'
      ? 'bg-gray-100 text-gray-700'
      : 'bg-gray-50 text-gray-400 line-through';
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      {items.length === 0 ? (
        <p className="mt-2 text-xs text-gray-400">{emptyText}</p>
      ) : (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {items.map((skill) => (
            <li key={skill.id}>
              <span className={`relative inline-flex items-center gap-1 ${chipClass} text-xs px-2 py-0.5 rounded`}>
                <span>{skill.name}</span>
                {skill.active ? (
                  <button
                    type="button"
                    onClick={() => setReasonForSkill((cur) => (cur === skill.id ? null : skill.id))}
                    title="Remove skill (declare a reason)"
                    aria-label={`Remove ${skill.name}`}
                    aria-expanded={reasonForSkill === skill.id}
                    className="ml-0.5 leading-none font-semibold text-gray-400 hover:text-gray-700 no-underline"
                  >
                    ×
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onToggle(skill.id, true)}
                    title="Add skill back"
                    aria-label={`Add ${skill.name} back`}
                    className="ml-0.5 leading-none font-semibold text-gray-400 hover:text-gray-700 no-underline"
                  >
                    +
                  </button>
                )}

                {reasonForSkill === skill.id && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-20 mt-1 w-52 overflow-hidden rounded-md border border-gray-200 bg-white py-1 text-left shadow-lg"
                  >
                    <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      Why remove?
                    </p>
                    {SKILL_REMOVAL_REASONS.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setReasonForSkill(null);
                          onToggle(skill.id, false, r.value);
                        }}
                        className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 no-underline hover:bg-gray-50"
                      >
                        {r.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => setReasonForSkill(null)}
                      className="block w-full border-t border-gray-100 px-3 py-1.5 text-left text-xs text-gray-400 no-underline hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
